import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { AUTH_STORAGE_KEYS } from '@/types/auth'
import type { ApiResponse, AuthTokens, User } from '@/types'
import i18n, { DEFAULT_LANGUAGE } from '@/i18n'

// `VITE_API_BASE_URL` is the API *domain only* (no version segment). The API
// version is composed here so endpoint services stay version-less and a future
// v2 can be introduced via a separate instance without re-pointing env config.
export const API_VERSION = 'v1'
const API_DOMAIN = (
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8091'
).replace(/\/+$/, '')
export const API_BASE_URL = `${API_DOMAIN}/api/${API_VERSION}`

// The backend returns the authenticated user with a `roles` array (+ `permissions`),
// not a singular `role`. Normalize to a singular `role` (first role) so the route
// guards / nav visibility (which read `user.role`) work, while keeping the arrays.
// `permissions` is preserved as-is for module matrix enforcement (2.11.3).
export function normalizeUser<
  T extends { role?: unknown; roles?: unknown[]; permissions?: unknown },
>(user: T): T {
  if (
    user &&
    !user.role &&
    Array.isArray(user.roles) &&
    user.roles.length > 0
  ) {
    ;(user as { role?: unknown }).role = user.roles[0]
  }
  if (user && !Array.isArray(user.permissions)) {
    ;(user as { permissions?: string[] }).permissions = []
  }
  return user
}

// Auth token storage helpers (single source of truth for tokens / user).
export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN),
  getAccessTokenExpiresAt: () =>
    localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN_EXPIRES_AT),
  getRefreshToken: () => localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN),
  setTokens(tokens: AuthTokens) {
    if (tokens.user) normalizeUser(tokens.user)
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token)
    localStorage.setItem(
      AUTH_STORAGE_KEYS.ACCESS_TOKEN_EXPIRES_AT,
      tokens.access_token_expires_at
    )
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token)
    localStorage.setItem(
      AUTH_STORAGE_KEYS.REFRESH_TOKEN_EXPIRES_AT,
      tokens.refresh_token_expires_at
    )
    localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(tokens.user))
  },
  setUser(user: User) {
    const normalized = normalizeUser(user)
    localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(normalized))
    return normalized
  },
  clear() {
    Object.values(AUTH_STORAGE_KEYS).forEach(k => localStorage.removeItem(k))
  },
}

// Active UI language as a bare locale code (e.g. 'fr', not 'fr-FR'). The
// backend Localization middleware reads Accept-Language to localize responses,
// so this keeps server messages in sync with the frontend language.
export const currentLocale = (): string =>
  i18n.language?.split('-')[0] || DEFAULT_LANGUAGE

// Create axios instance bound to the seed-backend contract. Accept-Language is
// (re)set per request in the interceptor below so it always reflects the
// currently selected language.
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Accept-Language': DEFAULT_LANGUAGE,
  },
})

const ACCESS_TOKEN_REFRESH_SKEW_MS = 60_000
const AUTH_REFRESH_LOCK_NAME = 'tigerflow-auth-refresh'

const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/auth/refresh-token',
  '/auth/forgot-password',
  '/auth/reset-password',
]

function isPublicAuthCall(url?: string) {
  if (!url) return false
  return PUBLIC_AUTH_PATHS.some(path => url.includes(path))
}

function accessTokenNeedsRefresh(skewMs = ACCESS_TOKEN_REFRESH_SKEW_MS) {
  const expiresAt = tokenStorage.getAccessTokenExpiresAt()
  if (!expiresAt) return false

  const expiresAtMs = Date.parse(expiresAt)
  return Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now() + skewMs
}

function forceLogout() {
  tokenStorage.clear()
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

type RefreshOptions = {
  force?: boolean
  failedAccessToken?: string | null
}

let refreshPromise: Promise<string> | null = null

async function refreshAccessTokenUnlocked({
  force = false,
  failedAccessToken = null,
}: RefreshOptions = {}): Promise<string> {
  const accessToken = tokenStorage.getAccessToken()

  // Another tab may have refreshed while this tab was waiting for the lock.
  if (failedAccessToken && accessToken && accessToken !== failedAccessToken) {
    return accessToken
  }

  if (!accessToken) throw new Error('Access token is missing')
  if (!force && !accessTokenNeedsRefresh()) return accessToken

  const refreshToken = tokenStorage.getRefreshToken()
  if (!refreshToken) throw new Error('Refresh token is missing')

  try {
    // Bare axios call avoids interceptor recursion. The backend ties the
    // refresh token to its access token and accepts the expired access token
    // here to locate the pair.
    const { data } = await axios.post<ApiResponse<AuthTokens>>(
      `${API_BASE_URL}/auth/refresh-token`,
      { refresh_token: refreshToken },
      {
        headers: {
          Accept: 'application/json',
          'Accept-Language': currentLocale(),
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
    const tokens = data.data
    if (!tokens) throw new Error('Refresh failed')
    tokenStorage.setTokens(tokens)
    return tokens.access_token
  } catch (error) {
    // Without Web Locks support, another tab may still win the refresh race.
    // Its freshly stored token is safe to reuse instead of logging out.
    const latestAccessToken = tokenStorage.getAccessToken()
    if (latestAccessToken && latestAccessToken !== accessToken) {
      return latestAccessToken
    }
    throw error
  }
}

function refreshAccessToken(options: RefreshOptions = {}): Promise<string> {
  if (refreshPromise) return refreshPromise

  const run = () => refreshAccessTokenUnlocked(options)
  const lockManager =
    typeof navigator !== 'undefined' ? navigator.locks : undefined
  const pending = lockManager
    ? lockManager.request(AUTH_REFRESH_LOCK_NAME, run)
    : run()

  const sharedRefresh = pending.finally(() => {
    if (refreshPromise === sharedRefresh) refreshPromise = null
  })
  refreshPromise = sharedRefresh
  return sharedRefresh
}

function bearerTokenFrom(config: InternalAxiosRequestConfig) {
  const authorization = config.headers.Authorization
  if (typeof authorization !== 'string') return null
  return authorization.startsWith('Bearer ')
    ? authorization.slice('Bearer '.length)
    : null
}

// Refresh shortly before expiry, then attach the latest Bearer token. If the
// proactive refresh fails, let the request continue so the 401 fallback can
// make the final authentication decision.
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (
      !isPublicAuthCall(config.url) &&
      tokenStorage.getAccessToken() &&
      tokenStorage.getRefreshToken() &&
      accessTokenNeedsRefresh()
    ) {
      try {
        await refreshAccessToken()
      } catch {
        // The response interceptor remains the fallback for expired/revoked
        // sessions and handles logout only after the protected call fails.
      }
    }

    const token = tokenStorage.getAccessToken()
    if (token) config.headers.Authorization = `Bearer ${token}`
    config.headers['Accept-Language'] = currentLocale()
    return config
  },
  error => Promise.reject(error)
)

// --- 401 refresh-then-retry fallback ---

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    const isRefreshCall = originalRequest?.url?.includes('/auth/refresh-token')

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshCall
    ) {
      originalRequest._retry = true

      try {
        const token = await refreshAccessToken({
          force: true,
          failedAccessToken: bearerTokenFrom(originalRequest),
        })
        originalRequest.headers.Authorization = `Bearer ${token}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        forceLogout()
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Extract a human-readable message from a seed-envelope error response.
export function getApiErrorMessage(
  error: unknown,
  fallback = i18n.t('errors:generic')
): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiResponse<unknown> | undefined
    if (data?.errors) {
      const first = Object.values(data.errors)[0]
      if (first && first.length) return first[0]
    }
    if (data?.message) return data.message
    if (data?.detail) return data.detail
    if (!error.response) {
      return i18n.t('errors:network')
    }
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}

export function getApiFieldError(
  error: unknown,
  field: string
): string | undefined {
  if (!axios.isAxiosError(error)) return undefined

  const data = error.response?.data as ApiResponse<unknown> | undefined
  const messages = data?.errors?.[field]
  return messages?.[0]
}

export default apiClient
